import { useEffect, useRef, useState, useCallback } from 'react';

type Args = {
  started: boolean;
  isFocused: boolean;
  face: boolean;
  orient: boolean;
  targetWindow: Window;
  graceMs?: number;
  sessionStartMs?: number;
  sessionEndMs?: number;
  getFocusWeight?: () => number;
  freeze?: boolean;
  onBreak?: boolean;
};

type Output = {
  sessionSeconds: number;
  distractions: number[];
  nudgeActive: boolean;
  welcomeActive: boolean;
  focusedSeconds: number;
  percentFocused: number;
};

const useSessionManager = ({ started, isFocused, face, orient, targetWindow, graceMs = 30000, sessionStartMs, sessionEndMs, getFocusWeight, freeze = false, onBreak = false }: Args): Output => {
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [distractions, setDistractions] = useState<number[]>([]);
  const [nudgeActive, setNudgeActive] = useState(false);
  const [welcomeActive, setWelcomeActive] = useState(false);
  const graceRef = useRef<number | null>(null);
  const graceTimerRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [focusedSeconds, setFocusedSeconds] = useState(0);
  const [activeSeconds, setActiveSeconds] = useState(0);

  const ensureStyles = useCallback(() => {
    const d = targetWindow.document;
    if (!d.getElementById('session-manager-styles')) {
      const style = d.createElement('style');
      style.id = 'session-manager-styles';
      style.textContent = '.sm-nudge-border{box-shadow:0 0 0 4px rgba(255,210,0,.7) inset;transition:box-shadow 250ms ease}.sm-welcome-border{box-shadow:0 0 0 4px rgba(46,204,113,.7) inset;transition:box-shadow 250ms ease}';
      d.head.appendChild(style);
    }
  }, [targetWindow]);

  const playNudge = useCallback(async () => {
    try {
      const C = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!C) return;
      if (!audioCtxRef.current) audioCtxRef.current = new C();
      const ctx = audioCtxRef.current as AudioContext;
      await ctx.resume();
      const t0 = ctx.currentTime + 0.01;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, t0);
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.08, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.15);
    } catch {}
  }, []);

  const playWelcome = useCallback(async () => {
    try {
      const C = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!C) return;
      if (!audioCtxRef.current) audioCtxRef.current = new C();
      const ctx = audioCtxRef.current as AudioContext;
      await ctx.resume();
      const t0 = ctx.currentTime + 0.01;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(500, t0);
      osc.frequency.linearRampToValueAtTime(700, t0 + 0.25);
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.07, t0 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.36);
    } catch {}
  }, []);

  useEffect(() => {
    let timer: number | null = null;
    timer = window.setInterval(() => {
      if (sessionStartMs && sessionEndMs) {
        const now = Date.now();
        const dur = Math.max(0, Math.floor((sessionEndMs - sessionStartMs) / 1000));
        const el = Math.min(dur, Math.max(0, Math.floor((now - sessionStartMs) / 1000)));
        setSessionSeconds(el);
        if (now >= sessionStartMs && now <= sessionEndMs) {
          setActiveSeconds((s) => s + 1);
          if (isFocused) setFocusedSeconds((f) => f + Math.max(0, Math.min(1, getFocusWeight ? getFocusWeight() : 1)));
        }
      } else if (started) {
        setSessionSeconds((s) => s + 1);
        setActiveSeconds((s) => s + 1);
        if (isFocused) setFocusedSeconds((f) => f + Math.max(0, Math.min(1, getFocusWeight ? getFocusWeight() : 1)));
      }
    }, 1000);
    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [started, isFocused, sessionStartMs, sessionEndMs, getFocusWeight, freeze]);

  useEffect(() => {
    ensureStyles();
  }, [targetWindow, ensureStyles]);

  useEffect(() => {
    const docEl = targetWindow.document.body;
    if (onBreak) {
      // Suppress nudges/welcome during break
      if (graceTimerRef.current) {
        window.clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
        graceRef.current = null;
      }
      setWelcomeActive(false);
      setNudgeActive(false);
      docEl.classList.remove('sm-welcome-border');
      docEl.classList.remove('sm-nudge-border');
    } else if (!isFocused) {
      // Unfocused State
      docEl.classList.add('sm-unfocused-bg');
      
      // Play sound immediately on transition to unfocused (if not already handled)
      // We use a ref to track if we just transitioned, but here we just ensure it plays once per "session" of unfocus?
      // Actually, relying on the grace timer setup is a good "edge trigger" equivalent because it's null when we enter this block first time.
      if (!graceTimerRef.current) {
        // Play sound immediately when becoming unfocused
        playNudge();
        
        graceRef.current = Date.now();
        graceTimerRef.current = window.setTimeout(() => {
          if (!isFocused) {
            setDistractions((prev) => [...prev, Date.now()]);
            setNudgeActive(true);
            docEl.classList.add('sm-nudge-border');
            // playNudge(); // Removed repeated sound, only initial alert
            const off = window.setTimeout(() => {
              setNudgeActive(false);
              docEl.classList.remove('sm-nudge-border');
              window.clearTimeout(off);
            }, 1500);
          }
          graceTimerRef.current = null;
          graceRef.current = null;
        }, graceMs);
      }
    } else {
      // Focused State
      docEl.classList.remove('sm-unfocused-bg');
      
      if (face && orient) {
        if (graceTimerRef.current) {
          window.clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
          graceRef.current = null;
        }
        setWelcomeActive(true);
        const docEl2 = targetWindow.document.body;
        docEl2.classList.add('sm-welcome-border');
        // playWelcome(); // Removed "back to focus" sound per user request
        const off2 = window.setTimeout(() => {
          setWelcomeActive(false);
          docEl2.classList.remove('sm-welcome-border');
          window.clearTimeout(off2);
        }, 1200);
      }
    }
    return () => {};
  }, [isFocused, face, orient, targetWindow, graceMs, onBreak, playNudge, playWelcome]);
  const percentFocused = Math.min(100, Math.round(((focusedSeconds || 0) / Math.max(1, activeSeconds || 0)) * 100));
  return { sessionSeconds, distractions, nudgeActive, welcomeActive, focusedSeconds, percentFocused };
};

export default useSessionManager;
